import {AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {AbstractControl, FormGroup} from '@angular/forms';
import {Field} from '@app/interfaces';
import {pairwise, Subject} from "rxjs";
import {startWith, takeUntil} from "rxjs/operators";
import {FormHelperService} from "@app/util/form-helper.service";
import {firstKey, firstValue, isNullable} from "@app/util/object.helper";
import {SearchableSelectOption} from "@app/shared/components/searchable-select/searchable-select.component";

/**
 * Above this many options a native `<select>` becomes a scroll hunt, so the field renders
 * as a type-to-filter input instead. On a vehicle form that only catches the two country
 * lists (252 options each) and vehicle category (97) — everything shorter keeps the native
 * select, which is still the better control on mobile.
 */
const SEARCHABLE_FROM_CHOICES = 20;

@Component({
    selector: 'app-form-input-dropdown',
    templateUrl: './form-input-dropdown.component.html',
    styleUrls: ['./form-input-dropdown.component.scss'],
    standalone: false
})
export class FormInputDropdownComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() control: AbstractControl;
  @Input() formGroup: FormGroup;

  public outputChoices = [];
  public isWorkOrderItemsField = false;
  public searchOptions: SearchableSelectOption[] = [];
  public searchable = false;

  public field: Field<number>;

  private destroy$ = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef, private formHelperService: FormHelperService) {
  }

  @Input('field') set fieldInput(value: Field<number>) {
    this.field = value;

    // Check if this is a work order items field (by data_source or type_id)
    this.isWorkOrderItemsField = value?.config?.data_source === 'work_order_items'
      || value?.config?.type_id === 'briefd.document.workflows.work_order.task_name';

    this.prepareChoices(this.field);
  }

  ngOnInit() {
    this.control.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.detectChanges();
      });

    this.control.valueChanges
      .pipe(takeUntil(this.destroy$),
        startWith(null),
        pairwise()
      )
      .subscribe(([previousValue, value]) => {
        if (value !== previousValue) {
          this.setDropdownValue(value);
        }
      });

    if (this.control.value) {
      this.setDropdownValue(this.control.value);
    } else if (this.field.value) {
      this.setDropdownValue(this.field.value);
    } else if (this.field.config.default) {
      this.setDropdownValue(this.field.config.default);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  ngAfterViewInit() {
    // force value if allowNull is false and value is null
    if (this.field.config.allowNull === false
      && isNullable(this.control?.value)
      && isNullable(this.field.value)
      && !isNullable(this.field.config.default)) {
      this.control.setValue(this.outputChoices[0]);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.field?.currentValue) {
      if (this.control.value) {
        this.setDropdownValue(this.control.value);
      }
    }
  }

  setDropdownValue(value) {
    const targetKey = firstKey(value) || value;
    const targetValue = firstValue(value);

    for (const choice of this.outputChoices) {
      const optionsKey = firstKey(choice);
      if (targetKey === optionsKey || value === optionsKey) {
        this.control.setValue(choice);
        this.formHelperService.modifyOnSetFields(value, this.field, this.formGroup);
        return;
      }
    }

    // Search also by display values (e.g. for countries, or when keys are camelCased)
    for (const choice of this.outputChoices) {
      const optionsValue = firstValue(choice);
      if (targetKey === optionsValue || targetValue === optionsValue) {
        this.control.setValue(choice);
        this.formHelperService.modifyOnSetFields(value, this.field, this.formGroup);
        return;
      }
    }

    // No match, reset to null
    this.control.setValue(null);
    this.formHelperService.modifyOnSetFields(null, this.field, this.formGroup);
  }

  prepareChoices(field: Field<number>): void {
    this.buildChoices(field);

    const priorityKeys: string[] = field.config?.priorityList ?? [];

    this.searchOptions = this.outputChoices
      .filter(choice => !choice?.disabled)   // priorityList separator / disabled options aren't pickable
      .map(choice => ({
        value: firstKey(choice),
        label: firstValue(choice),
        // Tinted in the panel — being first in a 250-row list isn't noticeable
        // on its own (BRIEFD-7094).
        priority: priorityKeys.includes(firstKey(choice)),
      }));

    this.searchable = this.searchOptions.length > SEARCHABLE_FROM_CHOICES;
  }

  /**
   * Option matching for the native `<select>`: the same choice when it has the
   * same key, not only when it is the same object.
   *
   * `[ngValue]` compares by identity by default, and the control can hold a
   * choice that is not one of the current option objects: a new `field` input
   * rebuilds `outputChoices`, and a rebuilt form hands this component a new
   * control holding `{key: label}` from the response. Both happen when the
   * vehicle form swaps in its re-derived advertising tab while this field is
   * on screen, which is exactly the "Manjkajo podatki" entry jump: the saved
   * Stanje motorja / Glavna poškodba rendered blank while the control still
   * held them and the helper counted them as filled (BRIEFD-7573 QA B-F2).
   * Primitive values keep the identity rule they always had.
   */
  public readonly sameChoice = (option: unknown, value: unknown): boolean => {
    if (option === value) {
      return true;
    }
    if (!option || !value || typeof option !== 'object' || typeof value !== 'object') {
      return false;
    }
    const key = firstKey(option);
    return key !== null && key !== undefined && key === firstKey(value);
  };

  /** Key of the choice object currently held by the control, for the searchable input. */
  get selectedKey(): string | null {
    return this.control?.value ? firstKey(this.control.value) : null;
  }

  /**
   * A native `<select>` marks its control dirty on user input by itself; the searchable
   * input writes through `setValue()`, which doesn't. The new-vehicle form reads `dirty`
   * to decide whether a section counts as done, so mark it here — and only here, not in
   * `setDropdownValue()`, which also runs while populating the form.
   */
  onSearchSelected(value: string | null): void {
    this.setDropdownValue(value);
    this.control.markAsDirty();
  }

  private buildChoices(field: Field<number>): void {
    // `lazy_choices: true` fields (e.g. briefd.vehicle.margin_tax_rate) ship
    // without a `choices` array on the initial form load — the dropdown is
    // expected to fetch them on demand. Falling through to `.sort()` on
    // `undefined` throws and prevents the user from opening the dropdown
    // (the field then silently looks unsettable). Treat a missing choices
    // array as an empty list; the field renders + accepts focus.
    const choices = field.config?.choices ?? [];

    if (!choices.length) {
      this.outputChoices = [];
      return;
    }

    if (!field.config.priorityList) {
      choices.sort((a, b) => {
        const aKey = firstValue(a);
        const bKey = firstValue(b);
        return aKey.localeCompare(bKey);
      });

      this.outputChoices = choices;

      return;
    }

    const priorityKeys = field.config.priorityList;
    // `{}` seed matters: without it reduce() uses the first choice as the
    // accumulator, so that country's entry ends up being its label string
    // instead of its `{code: label}` pair and renders blank. Harmless while
    // priorityList was only used by short hand-configured lists; country
    // dropdowns hit it as soon as they got one (BRIEFD-7094).
    const choicesMap = choices.reduce((acc, item) => {
      const key = firstKey(item);
      acc[key] = item;
      return acc;
    }, {});

    const outputChoices = [];
    const priorityChoicesHash = {};

    priorityKeys.forEach(key => {
      priorityChoicesHash[key] = key;
      if (choicesMap[key]) {
        outputChoices.push(choicesMap[key]);
      }
    });

    outputChoices.push({disabled: '        '});

    Object.keys(choicesMap).forEach(key => {
      if (!priorityChoicesHash[key]) {
        outputChoices.push(choicesMap[key]);
      }
    });

    this.outputChoices = outputChoices;
  }

  protected readonly firstValue = firstValue;
}
